// src/App.js
// Main application component - Handles routing and auth state

import React, { useState, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth as firebaseAuth } from './firebaseConfig';

import Navbar from './components/Navbar';
import HomePage from './pages/HomePage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import ImageEditorPage from './pages/ImageEditorPage';
import ChatSimulatorPage from './pages/ChatSimulatorPage';
import ImageConverterPage from './pages/ImageConverterPage';
import ChatSlidesPage from './pages/ChatSlidesPage';
import VisionBoardPage from './pages/VisionBoardPage';
import PleaseSignInPage from './pages/PleaseSignInPage';
import BulkChatSlidesPage from './pages/BulkChatSlidesPage';
import ImageAnimatorPage from './pages/ImageAnimatorPage';
import ChatAnimatorPage from './pages/ChatAnimatorPage';
import ListingGeneratorPage from './pages/ListingGeneratorPage';
import PopCultureImageGenPage from './pages/PopCultureImageGenPage';
import ProductImageGenPage from './pages/ProductImageGenPage';
import AmazonCalculatorPage from './pages/AmazonCalculatorPage';

const googleProvider = new GoogleAuthProvider();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize from localStorage or default to 'home'
  const [currentPage, setCurrentPage] = useState(() => {
    return localStorage.getItem('current_page') || 'home';
  });

  // Save to localStorage whenever currentPage changes
  useEffect(() => {
    localStorage.setItem('current_page', currentPage);
  }, [currentPage]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      const protectedPages = ['account', 'imageEditor', 'chatSimulator', 'imageConverter', 'chatSlides', 'visionBoard', 'bulkChatSlides', 'imageAnimator', 'chatAnimator', 'listingGenerator', 'popCultureImageGen', 'productImageGen', 'amazonCalculator'];
      if (!currentUser && protectedPages.includes(currentPage)) {
        // setCurrentPage('home'); // Optionally redirect
      }
    });
    return () => {
      unsubscribe();
    }
  }, [currentPage]);

  const handleGoogleSignIn = async (redirectPage = 'account') => {
    try {
      setLoading(true);
      await signInWithPopup(firebaseAuth, googleProvider);
      setCurrentPage(redirectPage);
    } catch (error) {
      console.error("Error signing in with Google: ", error.message, error.code);
    }
  };

  const handleSignOut = async () => {
    try {
      setLoading(true);
      await signOut(firebaseAuth);
      setCurrentPage('home');
    } catch (error) {
      console.error("Error signing out: ", error.message);
    }
  };

  const handleNavigate = (page) => {
    if (typeof page === 'string') {
      setCurrentPage(page);
    } else {
      console.error("App.js: handleNavigate called with invalid page argument:", page);
    }
  };

  const renderPage = () => {
    if (loading) {
      return (
        <div className="flex-grow flex items-center justify-center">
          <p className="text-xl text-gray-400">Loading...</p>
        </div>
      );
    }
    switch (currentPage) {
      case 'home':
        return <HomePage onNavigate={handleNavigate} />;
      case 'account':
        return user ? <AccountSettingsPage user={user} /> : <PleaseSignInPage onSignIn={() => handleGoogleSignIn('account')} featureName="Account Settings" />;
      case 'imageEditor':
        return user ? <ImageEditorPage /> : <PleaseSignInPage onSignIn={() => handleGoogleSignIn('imageEditor')} featureName="Image Editor" />;
      case 'chatSimulator':
        return user ? <ChatSimulatorPage /> : <PleaseSignInPage onSignIn={() => handleGoogleSignIn('chatSimulator')} featureName="Chat Simulator Video Tool" />;
      case 'imageConverter':
        return user ? <ImageConverterPage user={user} /> : <PleaseSignInPage onSignIn={() => handleGoogleSignIn('imageConverter')} featureName="Image Converter" />;
      case 'chatSlides':
        return user ? <ChatSlidesPage /> : <PleaseSignInPage onSignIn={() => handleGoogleSignIn('chatSlides')} featureName="Chat Slides Creator" />;
      case 'visionBoard':
        return user ? <VisionBoardPage user={user} /> : <PleaseSignInPage onSignIn={() => handleGoogleSignIn('visionBoard')} featureName="Vision Board" />;
      case 'bulkChatSlides':
        return user ? <BulkChatSlidesPage /> : <PleaseSignInPage onSignIn={() => handleGoogleSignIn('bulkChatSlides')} featureName="Bulk Chat Slides Creator" />;
      case 'imageAnimator':
        return user ? <ImageAnimatorPage /> : <PleaseSignInPage onSignIn={() => handleGoogleSignIn('imageAnimator')} featureName="Image Animator" />;
      case 'chatAnimator':
        return user ? <ChatAnimatorPage /> : <PleaseSignInPage onSignIn={() => handleGoogleSignIn('chatAnimator')} featureName="Chat Animator" />;
      case 'listingGenerator':
        return user ? <ListingGeneratorPage /> : <PleaseSignInPage onSignIn={() => handleGoogleSignIn('listingGenerator')} featureName="Listing Generator" />;
      case 'popCultureImageGen':
        return user ? <PopCultureImageGenPage /> : <PleaseSignInPage onSignIn={() => handleGoogleSignIn('popCultureImageGen')} featureName="Pop Culture Image Generator" />;
      case 'productImageGen':
        return user ? <ProductImageGenPage /> : <PleaseSignInPage onSignIn={() => handleGoogleSignIn('productImageGen')} featureName="Product Image Generator" />;
      case 'amazonCalculator':
        return user ? <AmazonCalculatorPage user={user} /> : <PleaseSignInPage onSignIn={() => handleGoogleSignIn('amazonCalculator')} featureName="Amazon FBA/FBM Calculator" />;
      default:
        return <HomePage onNavigate={handleNavigate} />;
    }
  };

  return (
    <HelmetProvider>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-black text-white flex flex-col font-sans">
        <Navbar
          user={user}
          onSignIn={handleGoogleSignIn}
          onSignOut={handleSignOut}
          onNavigate={handleNavigate}
          currentPage={currentPage}
        />
        <main className="flex-grow container mx-auto px-2 sm:px-4 py-8 flex flex-col">
          {renderPage()}
        </main>
        <footer className="py-6 text-center mt-auto border-t border-gray-800">
          <p className="text-sm text-gray-400">
            JustAutomateIt - Your Digital Productivity Suite &copy; {new Date().getFullYear()}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Automate • Create • Innovate
          </p>
        </footer>
      </div>
    </HelmetProvider>
  );
}

export default App;